import type { ReactNode } from 'react';
import type { ButtonProps } from '../Button/Button';
import type { LinkButtonProps } from '../LinkButton/LinkButton';
import type { PopoverPlacementOptions } from '../Popover/Popover';
import type { TooltipPlacement } from '../Tooltip/Tooltip';

import { useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

import { getTestProps } from '../../lib/helpers';

import { CaretDownIcon } from '../../images/shapes';

import ActionMenu from '../ActionMenu/ActionMenu';
import Button from '../Button/Button';
import LinkButton from '../LinkButton/LinkButton';
import { PopoverPanelPlacement } from '../Popover/classes';
import Tooltip from '../Tooltip/Tooltip';

interface ActionButtonBaseProps {
  /**
   * Action menu items
   */
  menuItems: {
    key: string;
    label?: ReactNode;
    onClick?: () => void;
    icon?: ReactNode;
    additionalClasses?: string;
  }[];
  /**
   * Action button link
   */
  link?: string;
  /**
   * ActionButton content
   */
  children?: ReactNode;
  /**
   * Action menu test id
   */
  testId?: string;
  /**
   * Action menu placement
   */
  actionMenuPlacement?: PopoverPlacementOptions;
  /**
   * Tooltip text to display on hover
   */
  tooltip?: string;
  /**
   * Tooltip menu placement
   */
  tooltipPlacement?: TooltipPlacement;
  /**
   * Button additional classes
   */
  additionalClasses?: string;
  /**
   * Additional CSS classes for popover panel component
   */
  additionalPopoverPanelClasses?: string;
}

type ActionButtonProps =
  | (ActionButtonBaseProps &
      Omit<LinkButtonProps, keyof ActionButtonBaseProps> & {
        /**
         * Item link
         */
        link: string;
      })
  | (ActionButtonBaseProps &
      Omit<ButtonProps, keyof ActionButtonBaseProps> & {
        /**
         * Item link
         */
        link?: never;
      });

const ActionButton = ({
  menuItems,
  children = '',
  actionMenuPlacement = PopoverPanelPlacement.BottomRight,
  tooltip,
  tooltipPlacement,
  additionalClasses = '',
  additionalPopoverPanelClasses,
  testId = '',
  ...props
}: ActionButtonProps) => {
  const baseProps = useMemo(
    () => ({
      additionalChildrenClasses: 'flex items-center justify-between',
      additionalClasses: twMerge(
        'text-base 2xl:text-base pr-2 rounded-r-none',
        additionalClasses,
      ),
      ...getTestProps(testId, 'action-btn', 'testId'),
    }),
    [additionalClasses, testId],
  );

  return (
    <Tooltip tooltip={tooltip} tooltipPlacement={tooltipPlacement}>
      <div
        className="flex items-center relative"
        {...getTestProps(testId, 'action-button-container')}
      >
        {props.link ? (
          <LinkButton
            {...baseProps}
            {...(props as Omit<LinkButtonProps, keyof ActionButtonBaseProps>)}
          >
            {children}
          </LinkButton>
        ) : (
          <Button
            {...baseProps}
            {...(props as Omit<ButtonProps, keyof ActionButtonBaseProps>)}
          >
            {children}
          </Button>
        )}

        <ActionMenu
          menuItems={menuItems}
          actionMenuPlacement={actionMenuPlacement}
          button={
            <CaretDownIcon
              className={'h-3 w-3 text-white'}
              {...getTestProps(testId, 'open')}
            />
          }
          additionalClasses={twMerge(
            'rounded-l-none pl-2.5',
            additionalClasses,
          )}
          additionalActionMenuClasses="static"
          additionalPopoverPanelClasses={additionalPopoverPanelClasses}
          iconImage={undefined}
          disabled={props.disabled}
          buttonColor={props.buttonColor}
          buttonSize={props.buttonSize}
          buttonShadow={props.buttonShadow}
          iconColor={props.iconColor}
          iconPosition={props.iconPosition}
          {...getTestProps(testId)}
        />
      </div>
    </Tooltip>
  );
};

export default ActionButton;
